import React, { useState } from 'react';
import LoginForm from './LoginForm';
import Checkbox from '../atoms/checkbox/Checkbox';
import { CreditCard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export type ReservationFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  shippingAddress: string;
  province?: string;
  city?: string;
  cp?: string;
  password?: string;
  confirmPassword?: string;
  useUserData: boolean;
  showSignup: boolean;
  notes?: string | null;
  insuranceIncluded?: boolean;
};

interface Props {
  onSubmit: (values: ReservationFormValues) => void | Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  onError?: (err: unknown) => void;
}

  const ReservationForm: React.FC<Props> = ({ onSubmit, isLoading, isAuthenticated, login, onError }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [cp, setCp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [useUserData, setUseUserData] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showLoginInline, setShowLoginInline] = useState(() => !isAuthenticated);
  const [notes, setNotes] = useState<string | null>(null);
  const [insuranceIncluded, setInsuranceIncluded] = useState(true);
  const { user } = useAuth();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleUseUserDataChange = (checked: boolean) => {
    setUseUserData(checked);
    if (checked && user) {
      const u = user as unknown as Record<string, string>;
      const names = (u.name || '').split(' ');
      setFirstName(names[0] || '');
      setLastName(names.slice(1).join(' ') || '');
      setEmail(u.email || '');
      setPhone(u.phone || '');
      setShippingAddress(u.shippingAddress || '');
      setProvince(u.province || '');
      setCity(u.city || '');
      setCp(u.cp || '');
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setShippingAddress('');
      setProvince('');
      setCity('');
      setCp('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!firstName.trim()) nextErrors.firstName = 'Nombre requerido';
    if (!lastName.trim()) nextErrors.lastName = 'Apellido requerido';
    if (!email.trim() || !/.+@.+\..+/.test(email)) nextErrors.email = 'Email válido requerido';
    const phoneClean = phone.replace(/[\s-()+]/g, '');
    if (!phone.trim()) nextErrors.phone = 'Teléfono requerido';
    else if (!/^[0-9]{6,15}$/.test(phoneClean)) nextErrors.phone = 'Teléfono no válido';
    if (!shippingAddress.trim()) nextErrors.shippingAddress = 'Dirección requerida';
    if (cp && !/^\d{4,5}$/.test(cp)) nextErrors.cp = 'Código postal no válido';

    // Validación de contraseña cuando el usuario se registra en este flujo
    if (!isAuthenticated && showSignup) {
      const pwd = password ?? '';
      const pwdConfirm = confirmPassword ?? '';
      const complexity = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

      if (!pwd) {
        nextErrors.password = 'Contraseña requerida';
      } else if (!complexity.test(pwd)) {
        nextErrors.password = 'La contraseña debe tener al menos 8 caracteres, incluir una letra mayúscula, una letra minúscula y un número';
      }

      if (!pwdConfirm) {
        nextErrors.confirmPassword = 'Confirmar contraseña requerida';
      } else if (pwd !== pwdConfirm) {
        nextErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    await onSubmit({
      firstName,
      lastName,
      email,
      phone,
      shippingAddress,
      province,
      city,
      cp,
      password,
      confirmPassword,
      useUserData,
      showSignup,
      notes,
      insuranceIncluded,
    });
  };

  return (
    <>
      {/* Información de Contacto */}
      <div>
        <div className="flex items-center justify-between mb-4 ">
          <h3 className="text-lg font-medium">Información de Contacto</h3>
          {isAuthenticated && (
            <label className="flex items-center space-x-2 text-sm">
              <Checkbox
                checked={useUserData}
                onChange={(e) => handleUseUserDataChange((e.target as HTMLInputElement).checked)}
              />
              <span>Usar mis datos de usuario</span>
            </label>
          )}
        </div>

        {!isAuthenticated && (
          <div className="p-4 mb-4 border border-blue-100 rounded bg-blue-50">
            <div className="flex items-end justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-800">¿No tienes cuenta?</h3>
                <p className="text-xs text-blue-700">Crea una cuenta rápida para gestionar tus reservas.</p>
                <label className="flex items-center mt-3 space-x-3 text-sm font-medium text-blue-800">
                  <Checkbox
                    checked={showSignup}
                    onChange={(e) => {
                      const checked = (e.target as HTMLInputElement).checked;
                      setShowSignup(checked);
                      setShowLoginInline(!checked);
                    }}
                  />
                  <span>Registrarme aquí</span>
                </label>
              </div>
            </div>
            <hr className="my-4 border-blue-200" />
            {showLoginInline && (
              <div className="mt-3">
                <LoginForm
                  isLoading={isLoading}
                  onSubmit={async (emailArg: string, passwordArg: string) => {
                    try {
                      await login(emailArg, passwordArg);
                      // hide inline login after successful sign in
                      setShowLoginInline(false);
                      setShowSignup(false);
                    } catch (err) {
                      // Propaga el error al handler global si se proporcionó
                      if (onError) onError(err);
                      // también dejamos el console.error para depuración
                      console.error(err);
                    }
                  }}
                />
              </div>
            )}
            {!showLoginInline && (
              <div className="mt-3 text-xs text-blue-700">
                <p>
                  Al registrarte aquí no serás redirigido a otra página; tu cuenta se creará y la reserva se
                  procesará en el mismo flujo.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Nombre <span className="text-red-600">*</span></label>
            <input
              type="text"
              required
              placeholder="Nombre"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.firstName ? 'border border-red-500' : 'border border-gray-300'}`}
            />

            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Apellido <span className="text-red-600">*</span></label>
            <input
              type="text"
              required
              placeholder="Apellido"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.lastName ? 'border border-red-500' : 'border border-gray-300'}`}
            />

            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Email <span className="text-red-600">*</span></label>
            <input
              type="email"
              required
              placeholder="tu@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border border-red-500' : 'border border-gray-300'}`}
            />

            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Teléfono <span className="text-red-600">*</span></label>
            <input
              type="tel"
              required
              placeholder="612 345 678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.phone ? 'border border-red-500' : 'border border-gray-300'}`}
            />

            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          </div>
          <div className="md:col-span-2">
            <label className="block mb-1 text-sm font-medium text-gray-700">Dirección de Envío <span className="text-red-600">*</span></label>
            <input
              type="text"
              required
              placeholder="Calle Ejemplo 123, 1ºA"
              value={shippingAddress}
              onChange={(e) => setShippingAddress(e.target.value)}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.shippingAddress ? 'border border-red-500' : 'border border-gray-300'}`}
            />

            {errors.shippingAddress && <p className="mt-1 text-sm text-red-600">{errors.shippingAddress}</p>}
          </div>

          <div className="grid grid-cols-1 gap-3 md:col-span-2 md:grid-cols-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Provincia</label>
              <input
                type="text"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.province ? 'border border-red-500' : 'border border-gray-300'}`}
              />

            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Ciudad</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.city ? 'border border-red-500' : 'border border-gray-300'}`}
              />

            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Código Postal</label>
              <input
                type="text"
                placeholder="46001"
                value={cp}
                onChange={(e) => setCp(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.cp ? 'border border-red-500' : 'border border-gray-300'}`}
              />

              {errors.cp && <p className="mt-1 text-sm text-red-600">{errors.cp}</p>}
            </div>
          </div>

          {/* Campos de registro si el usuario no está autenticado */}
          {!isAuthenticated && showSignup && (
            <>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Contraseña <span className="text-red-600">*</span></label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.password ? 'border border-red-500' : 'border border-gray-300'}`}
                  minLength={6}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Confirmar Contraseña <span className="text-red-600">*</span></label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.confirmPassword ? 'border border-red-500' : 'border border-gray-300'}`}
                  minLength={6}
                />
              </div>
              {errors.password && <p className="col-span-2 mt-1 text-sm text-red-600">{errors.password}</p>}
              {errors.confirmPassword && <p className="col-span-2 mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
            </>
          )}
        </div>

        {/* Información de Pago */}
        <div>
          <h3 className="mb-4 text-lg font-medium">Información de Pago</h3>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Número de Tarjeta</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="1234 5678 9012 3456"
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <CreditCard className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2" size={18} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Fecha de Expiración</label>
                <input
                  type="text"
                  required
                  placeholder="MM/AA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">CVV</label>
                <input
                  type="text"
                  required
                  placeholder="123"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Opciones extras */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">Notas (opcional)</label>
          <textarea
            value={notes ?? ''}
            onChange={(e) => setNotes(e.target.value || null)}
            placeholder="Añade indicaciones de entrega u observaciones"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <label className="flex items-center mt-3 space-x-3 text-sm">
            <Checkbox checked={insuranceIncluded} onChange={(e) => setInsuranceIncluded((e.target as HTMLInputElement).checked)} />
            <span>Incluir seguro de alquiler</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full bg-blue-600 text-white py-3 rounded-lg font-medium transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'
            }`}
        >
          {isLoading ? 'Procesando...' : !isAuthenticated && showSignup ? 'Crear Cuenta y Confirmar Reserva' : 'Confirmar Reserva'}
        </button>
      </form >
    </>
  );
};

export default ReservationForm;
